import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SiteLogo } from "@/components/SiteLogo";
import { loginAdmin } from "@/lib/api";
import { isAuthenticated, setToken } from "@/lib/auth";

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/admin";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated()) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { access_token } = await loginAdmin(username, password);
      setToken(access_token);
      navigate(from, { replace: true });
    } catch {
      setError("Invalid username or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4" data-testid="admin-login-page">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-white border border-black/10 p-8 space-y-5"
      >
        <div className="flex flex-col items-center text-center">
          <SiteLogo className="h-12 w-auto mb-4" />
          <h1 className="text-2xl font-semibold tracking-tight">Admin login</h1>
          <p className="text-sm text-vg-gray mt-1">Sign in to manage products</p>
        </div>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Username</span>
          <input
            className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            data-testid="admin-login-username"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Password</span>
          <input
            type="password"
            className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="admin-login-password"
          />
        </label>

        {error && (
          <p className="text-sm text-red-600" data-testid="admin-login-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full h-10 bg-vg-navy text-white text-sm font-medium hover:bg-vg-cyan disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

export default AdminLoginPage;
